import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, DollarSign, RefreshCw, FileText, Download, LogOut, CheckCircle2, Clock, AlertCircle, Ban, X } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ThemeToggle from "@/components/site/ThemeToggle";

const STATUS_META = {
  confirmed: { label: "PAID", icon: CheckCircle2, color: "text-[#005EEA]" },
  pending: { label: "PENDING", icon: Clock, color: "text-yellow-400" },
  expired: { label: "EXPIRED", icon: AlertCircle, color: "text-white/40" },
  cancelled: { label: "CANCELLED", icon: Ban, color: "text-white/40" },
  refunded: { label: "REFUNDED", icon: RefreshCw, color: "text-orange-400" },
};

function money(cents, currency = "CAD") {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminClinics() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [selected, setSelected] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waiverModal, setWaiverModal] = useState(null);

  const load = async () => {
    try {
      await api.get("/auth/me");
      const { data } = await api.get("/admin/clinics");
      setClinics(data);
      if (data[0] && !selected) setSelected(data[0]);
    } catch (e) {
      setToken(null);
      navigate("/login");
    } finally { setLoading(false); }
  };

  const loadDetail = async (c) => {
    setSelected(c);
    try {
      const [regsRes, revRes] = await Promise.all([
        api.get(`/admin/clinics/${c.id}/registrations`),
        api.get(`/admin/clinics/${c.id}/revenue`),
      ]);
      setRegistrations(regsRes.data);
      setRevenue(revRes.data);
    } catch (e) { toast("Could not load clinic details"); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (selected) loadDetail(selected);
  }, [selected?.id]); // eslint-disable-line

  const refundReg = async (reg) => {
    if (!window.confirm(`Mark ${reg.first_name} ${reg.last_name} as refunded?\n\n⚠️ You must issue the refund in Square separately — this only updates our records.`)) return;
    try {
      await api.post(`/admin/clinics/${selected.id}/registrations/${reg.id}/refund`);
      toast("Marked as refunded. Issue the Square refund from your Square Dashboard.");
      loadDetail(selected);
    } catch { toast("Could not update."); }
  };

  const openWaivers = async (reg) => {
    try {
      const { data } = await api.get(`/admin/clinics/${selected.id}/waivers/${reg.id}`);
      setWaiverModal({ reg, waivers: data });
    } catch { toast("Could not load waivers."); }
  };

  const exportCSV = () => {
    if (registrations.length === 0) return toast("No registrations to export");
    const rows = [
      ["First", "Last", "Email", "Phone", "Status", "Amount", "Paid At", "Emergency Name", "Emergency Phone", "Experience", "Injuries", "Considerations", "Created"],
      ...registrations.map((r) => [
        r.first_name, r.last_name, r.email, r.phone, r.status,
        money(r.amount_cents), r.paid_at || "", r.emergency_contact_name, r.emergency_contact_phone,
        r.fitness_experience || "", r.injuries_notes || "", r.training_considerations || "",
        r.created_at,
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")),
    ];
    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selected.slug}-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => { setToken(null); navigate("/login"); };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#050505] text-white/40 font-mono-accent text-xs">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-clinics-page">
      <header className="border-b border-[#161616] px-5 sm:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="font-mono-accent text-[10px] text-[#A0A0A0] hover:text-white uppercase tracking-wider inline-flex items-center gap-2" data-testid="admin-back-link">
            <ArrowLeft size={13} /> Admin
          </Link>
          <span className="font-display text-lg">CLINICS</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button onClick={logout} className="font-mono-accent text-xs text-[#A0A0A0] hover:text-white inline-flex items-center gap-2" data-testid="admin-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-10 py-8">
        {/* Clinic switcher */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="clinic-switcher">
          {clinics.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c)}
              data-testid={`clinic-tab-${c.slug}`}
              className={`font-mono-accent text-xs px-4 py-3 border transition-colors ${
                selected?.id === c.id ? "border-[#005EEA] bg-[#005EEA]/10 text-white" : "border-[#222] text-[#A0A0A0] hover:border-white/30 hover:text-white"
              }`}
            >
              {c.name.split("—")[0].trim()}
            </button>
          ))}
        </div>

        {selected && (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Tile icon={Users} label="Confirmed" value={`${selected.confirmed_count} / ${selected.capacity}`} sub={`${selected.available} spots left`} testid="kpi-capacity" />
              <Tile icon={Clock} label="Pending holds" value={selected.pending_count} sub="releases after 10 min" testid="kpi-pending" />
              <Tile icon={DollarSign} label="Gross revenue" value={money(revenue?.gross_cents)} sub={`${revenue?.confirmed_count || 0} paid`} testid="kpi-gross" />
              <Tile icon={DollarSign} label="Net to split" value={money(revenue?.base_portion_cents)} sub="after HST + fees" testid="kpi-net" />
            </div>

            {/* Revenue breakdown */}
            {revenue && revenue.confirmed_count > 0 && (
              <div className="border border-[#222] bg-[#0b0b0b] p-6 mb-8" data-testid="revenue-breakdown">
                <h3 className="font-mono-accent text-xs text-[#005EEA] tracking-wider mb-4">70/30 REVENUE BREAKDOWN</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <RevItem label="Gross" value={money(revenue.gross_cents)} />
                  <RevItem label="Processing" value={`− ${money(revenue.fees_cents)}`} muted />
                  <RevItem label="Net" value={money(revenue.net_cents)} />
                  <RevItem label="GFWJC (70%)" value={money(revenue.gfwjc_share_cents)} highlight />
                  <RevItem label="ALKAME (30%)" value={money(revenue.alkame_share_cents)} />
                </div>
                <p className="font-mono-accent text-[10px] text-white/40 mt-4">
                  Internal reporting only. Base portion = net ÷ 1.13 (strips HST passthrough).
                  Partner payouts are manual via Square Dashboard.
                </p>
              </div>
            )}

            {/* Clinic meta */}
            <div className="border border-[#222] bg-[#0b0b0b] p-5 mb-8 text-sm" data-testid="clinic-meta">
              <div className="grid md:grid-cols-3 gap-3 text-white/70">
                <div><span className="font-mono-accent text-[10px] text-white/40 block">LOCATION</span>{selected.location_name} · {selected.location_address || "—"}</div>
                <div><span className="font-mono-accent text-[10px] text-white/40 block">DATES</span>{selected.start_date} → {selected.end_date} · {selected.day_of_week} · {selected.time_slot}</div>
                <div><span className="font-mono-accent text-[10px] text-white/40 block">ALKAME WAIVER</span>{selected.alkame_waiver_url ? <a href={selected.alkame_waiver_url} target="_blank" rel="noopener noreferrer" className="text-[#005EEA] underline">Open link ↗</a> : "— not configured —"}</div>
              </div>
            </div>

            {/* Registrations table */}
            <div className="border border-[#222] bg-[#0b0b0b]" data-testid="registrations-section">
              <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-xl flex items-center gap-3"><Users size={18} className="text-[#005EEA]" /> Registrations</h2>
                <div className="flex gap-2">
                  <button onClick={() => loadDetail(selected)} className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-3 py-2 hover:text-white hover:border-white/30 inline-flex items-center gap-2" data-testid="refresh-regs">
                    <RefreshCw size={13} /> Refresh
                  </button>
                  <button onClick={exportCSV} className="font-mono-accent text-xs bg-[#005EEA] text-white px-3 py-2 hover:bg-[#0047b3] inline-flex items-center gap-2" data-testid="export-csv">
                    <Download size={13} /> Export CSV
                  </button>
                </div>
              </div>
              {registrations.length === 0 ? (
                <p className="p-10 text-center text-[#666] font-mono-accent text-xs" data-testid="empty-regs">No registrations yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#222] font-mono-accent text-[10px] text-[#666]">
                        <th className="px-4 py-3 font-normal">Name</th>
                        <th className="px-4 py-3 font-normal">Email</th>
                        <th className="px-4 py-3 font-normal">Phone</th>
                        <th className="px-4 py-3 font-normal">Status</th>
                        <th className="px-4 py-3 font-normal">Amount</th>
                        <th className="px-4 py-3 font-normal">Paid</th>
                        <th className="px-4 py-3 font-normal text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.pending;
                        const Icon = meta.icon;
                        return (
                          <tr key={r.id} className="border-b border-[#161616] hover:bg-[#111]" data-testid={`reg-row-${r.id}`}>
                            <td className="px-4 py-3 text-white">{r.first_name} {r.last_name}</td>
                            <td className="px-4 py-3 text-white/70 text-xs">{r.email}</td>
                            <td className="px-4 py-3 text-white/70 text-xs">{r.phone}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1.5 font-mono-accent text-[10px] ${meta.color}`}>
                                <Icon size={12} /> {meta.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-mono-accent text-xs">{money(r.amount_cents)}</td>
                            <td className="px-4 py-3 text-xs text-white/60">{r.paid_at ? fmtDate(r.paid_at) : "—"}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1.5">
                                <button onClick={() => openWaivers(r)} className="p-1.5 text-[#A0A0A0] hover:text-white hover:bg-[#005EEA] transition-colors" title="View waivers" data-testid={`view-waivers-${r.id}`}>
                                  <FileText size={13} />
                                </button>
                                {r.status === "confirmed" && (
                                  <button onClick={() => refundReg(r)} className="p-1.5 text-[#A0A0A0] hover:text-white hover:bg-orange-500 transition-colors" title="Mark refunded" data-testid={`refund-${r.id}`}>
                                    <RefreshCw size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Waiver viewer modal */}
      <Dialog open={!!waiverModal} onOpenChange={(o) => !o && setWaiverModal(null)}>
        <DialogContent className="bg-[#0b0b0b] border border-[#222] text-white rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="waiver-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Signed Waivers — {waiverModal?.reg?.first_name} {waiverModal?.reg?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {waiverModal?.waivers?.map((w) => (
              <div key={w.id} className="border border-[#222] bg-black/40 p-4" data-testid={`waiver-${w.kind}`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-wider">
                      {w.kind.toUpperCase()} · v{w.version}
                    </p>
                    <h4 className="font-display text-lg uppercase mt-1">Signed by {w.signature_name}</h4>
                    <p className="text-xs text-white/50 mt-1">Signed {fmtDate(w.signed_at)}</p>
                  </div>
                  {w.waiver_url && (
                    <a href={w.waiver_url} target="_blank" rel="noopener noreferrer" className="font-mono-accent text-[10px] text-[#005EEA] hover:underline inline-flex items-center gap-1">
                      Open external ↗
                    </a>
                  )}
                </div>
                {w.waiver_text && (
                  <details className="mt-3">
                    <summary className="font-mono-accent text-[10px] text-white/50 cursor-pointer hover:text-white">Show full waiver text</summary>
                    <pre className="mt-3 text-xs text-white/60 whitespace-pre-wrap font-sans leading-relaxed">{w.waiver_text}</pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-[#222] bg-[#0b0b0b] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-accent text-[10px] text-white/40 tracking-wider">{label}</span>
        <Icon size={14} className="text-[#005EEA]" />
      </div>
      <div className="font-display text-3xl">{value}</div>
      {sub && <div className="font-mono-accent text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function RevItem({ label, value, muted, highlight }) {
  return (
    <div>
      <div className="font-mono-accent text-[10px] text-white/40 mb-1 tracking-wider">{label}</div>
      <div className={`font-display text-lg ${muted ? "text-white/50" : highlight ? "text-[#005EEA]" : "text-white"}`}>{value}</div>
    </div>
  );
}
